import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';

export type ChaosExperimentType = 'PodChaos' | 'NetworkChaos' | 'StressChaos' | 'IOChaos';
export type ChaosStatus = 'idle' | 'running' | 'completed' | 'failed' | 'paused' | 'unknown';

export interface ChaosExperiment {
  name: string;
  kind: ChaosExperimentType;
  action?: string;
  status: ChaosStatus;
  startTime?: string;
  duration?: string;
}

export interface PodChaosConfig {
  action: 'pod-kill' | 'pod-failure' | 'container-kill';
  duration: string;
}

export interface NetworkChaosConfig {
  action: 'delay' | 'loss' | 'partition' | 'bandwidth';
  delay?: string;
  loss?: string;
  duration: string;
}

export interface StressChaosConfig {
  cpuWorkers: number;
  memorySize: string;
  duration: string;
}

export interface IOChaosConfig {
  action: 'delay' | 'fault' | 'attrOverride';
  delay?: string;
  duration: string;
}

export type ChaosConfig = PodChaosConfig | NetworkChaosConfig | StressChaosConfig | IOChaosConfig;

interface UseChaosExperimentResult {
  experiments: ChaosExperiment[];
  loading: boolean;
  error: Error | undefined;
  trigger: (kind: ChaosExperimentType, config: ChaosConfig) => Promise<void>;
  kill: (experimentName: string, kind: ChaosExperimentType) => Promise<void>;
  refresh: () => void;
}

const CHAOS_RESOURCES: Record<ChaosExperimentType, string> = {
  PodChaos: 'podchaos',
  NetworkChaos: 'networkchaos',
  StressChaos: 'stresschaos',
  IOChaos: 'iochaos',
};

function mapPhase(phase?: string): ChaosStatus {
  switch (phase?.toLowerCase()) {
    case 'running':
    case 'injecting':
    case 'allinjected':
    case 'run':
      return 'running';
    case 'finished':
    case 'allrecovered':
    case 'stop':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'paused':
      return 'paused';
    default:
      return 'idle';
  }
}

function parseDurationMs(d: string): number {
  const match = d.match(/^(\d+)(s|m|h)$/);
  if (!match) return 0;
  const val = parseInt(match[1], 10);
  switch (match[2]) {
    case 's': return val * 1000;
    case 'm': return val * 60 * 1000;
    case 'h': return val * 3600 * 1000;
    default: return 0;
  }
}

function resolveStatus(item: any): ChaosStatus {
  const conditions: any[] = item.status?.conditions ?? [];
  const allRecovered = conditions.some(
    (c: any) => c.type === 'AllRecovered' && c.status === 'True',
  );
  const allInjected = conditions.some(
    (c: any) => c.type === 'AllInjected' && c.status === 'True',
  );
  const paused = conditions.some(
    (c: any) => c.type === 'Paused' && c.status === 'True',
  );

  if (allRecovered) return 'completed';
  if (paused) return 'paused';

  // For one-shot actions like pod-kill, desiredPhase stays "Run" even after
  // the pod has been killed and recreated. Detect completion by checking
  // whether the experiment duration has elapsed since creation.
  const creationTime = item.metadata?.creationTimestamp
    ? new Date(item.metadata.creationTimestamp).getTime()
    : 0;
  const durationMs = parseDurationMs(item.spec?.duration ?? '');
  if (creationTime > 0 && durationMs > 0 && Date.now() - creationTime > durationMs) {
    return 'completed';
  }

  if (allInjected) return 'running';

  const phase = item.status?.experiment?.desiredPhase ?? item.status?.experiment?.phase;
  return mapPhase(phase);
}

function buildChaosSpec(
  kind: ChaosExperimentType,
  config: ChaosConfig,
  namespace: string,
  labelSelector: string,
): Record<string, unknown> {
  const base = {
    selector: {
      namespaces: [namespace],
      labelSelectors: parseLabelSelector(labelSelector),
    },
    mode: 'one',
    duration: config.duration,
  };

  switch (kind) {
    case 'PodChaos':
      return { ...base, action: (config as PodChaosConfig).action };
    case 'NetworkChaos': {
      const nc = config as NetworkChaosConfig;
      const spec: Record<string, unknown> = { ...base, action: nc.action, direction: 'to' };
      if (nc.action === 'delay' && nc.delay) spec.delay = { latency: nc.delay };
      if (nc.action === 'loss' && nc.loss) spec.loss = { loss: nc.loss };
      return spec;
    }
    case 'StressChaos': {
      const sc = config as StressChaosConfig;
      return {
        ...base,
        stressors: {
          cpu: { workers: sc.cpuWorkers },
          memory: { size: sc.memorySize },
        },
      };
    }
    case 'IOChaos': {
      const ic = config as IOChaosConfig;
      const spec: Record<string, unknown> = { ...base, action: ic.action, volumePath: '/' };
      if (ic.action === 'delay' && ic.delay) spec.delay = ic.delay;
      return spec;
    }
    default:
      return base;
  }
}

function parseLabelSelector(selector: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const pair of selector.split(',')) {
    const [key, val] = pair.split('=');
    if (key && val) result[key.trim()] = val.trim();
  }
  return result;
}

export function useChaosExperiment(entity: Entity): UseChaosExperimentResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [experiments, setExperiments] = useState<ChaosExperiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const mountedRef = useRef(true);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const annotations = entity.metadata.annotations ?? {};
  const namespace = annotations['backstage.io/kubernetes-namespace'] ?? 'demo-apps';
  const targetLabel = annotations['backstage.io/kubernetes-label-selector'] ?? `app=${entity.metadata.name}`;

  const fetchExperiments = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const allExperiments: ChaosExperiment[] = [];

      for (const [kind, resource] of Object.entries(CHAOS_RESOURCES)) {
        try {
          const resp = await fetchApi.fetch(
            `${baseUrl}/proxy/apis/chaos-mesh.org/v1alpha1/namespaces/${encodeURIComponent(namespace)}/${resource}`,
          );
          if (resp.ok) {
            const data = await resp.json();
            for (const item of data.items ?? []) {
              allExperiments.push({
                name: item.metadata?.name ?? 'unknown',
                kind: kind as ChaosExperimentType,
                action: item.spec?.action,
                status: resolveStatus(item),
                startTime: item.metadata?.creationTimestamp,
                duration: item.spec?.duration,
              });
            }
          }
        } catch {
          // CRD might not exist — degrade gracefully
        }
      }

      if (mountedRef.current) {
        setExperiments(
          allExperiments.sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? '')),
        );
        setError(undefined);

        const hasRunning = allExperiments.some(e => e.status === 'running');
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(fetchExperiments, hasRunning ? 3000 : 15000);
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [discoveryApi, fetchApi, namespace]);

  const trigger = useCallback(
    async (kind: ChaosExperimentType, config: ChaosConfig) => {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const resource = CHAOS_RESOURCES[kind];
      const timestamp = Date.now();
      const name = `${resource}-${entity.metadata.name}-${timestamp}`;

      const body = {
        apiVersion: 'chaos-mesh.org/v1alpha1',
        kind,
        metadata: {
          name,
          namespace,
          labels: {
            app: entity.metadata.name,
            'backstage.io/component': entity.metadata.name,
            'app.kubernetes.io/managed-by': 'backstage',
          },
        },
        spec: buildChaosSpec(kind, config, namespace, targetLabel),
      };

      const resp = await fetchApi.fetch(
        `${baseUrl}/proxy/apis/chaos-mesh.org/v1alpha1/namespaces/${encodeURIComponent(namespace)}/${resource}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Failed to create ${kind}: ${resp.status} ${text}`);
      }

      await fetchExperiments();
    },
    [discoveryApi, fetchApi, namespace, targetLabel, entity.metadata.name, fetchExperiments],
  );

  const kill = useCallback(
    async (experimentName: string, kind: ChaosExperimentType) => {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const resource = CHAOS_RESOURCES[kind];

      const resp = await fetchApi.fetch(
        `${baseUrl}/proxy/apis/chaos-mesh.org/v1alpha1/namespaces/${encodeURIComponent(namespace)}/${resource}/${encodeURIComponent(experimentName)}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
      );

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Failed to delete ${kind}: ${resp.status} ${text}`);
      }

      await fetchExperiments();
    },
    [discoveryApi, fetchApi, namespace, fetchExperiments],
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchExperiments();
    intervalRef.current = setInterval(fetchExperiments, 15000);
    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
    };
  }, [fetchExperiments]);

  return { experiments, loading, error, trigger, kill, refresh: fetchExperiments };
}
